import set from 'lodash/set';

import { ArraySchema, EntitySchema } from 'Lib/schemas';

import { PROFILE_ENTITY_KEY } from './entityKeys';

export const GENDER_MALE = 0;
export const GENDER_FEMALE = 1;
export const GENDER_UNSPECIFIED = -1;
export const GENDER_BEYOND_BINARY = 2;

export const genderIsMale = (gender: number): boolean => gender === GENDER_MALE;
export const genderIsFemale = (gender: number): boolean => gender === GENDER_FEMALE;

/**
 * Profile Entity
 * ==============
 *
 * This entity stores the information for a single profile in the recs stack or the read-only copy
 * of the logged-in user's profile.
 *
 * The "outer wrapper" is synthesized from multiple data sources and specific to a PAIR of users, so
 * the rec entity for the same profile will look different depending on the logged in user. Luckily,
 * the client isn't concerned about that.
 */
export const profileEntity = new EntitySchema(
  PROFILE_ENTITY_KEY,
  {
    /**
     * We can compose schemas to create relations between entities. Although we're not using this yet,
     * this enables us to avoid duplicating shared relational data or simply managing relations with
     * data sources like Facebook and Instagram in a more organized way.
     */
  },
  {
    /**
     * For our puposes, we can treat the user ID as the rec ID, since it's contextual to the logged in
     * user and we will throw away this data and refetch it if the user logs out and logs back in.
     */
    idAttribute(value: any) {
      return value.user?.id || value.user?._id || value._id || value._id;
    },

    /**
     * Entity schemas can be nested to allow us to slice up and compose schemas, but process strategy
     * allows us to "massage" and transform the data when we're doing more cleanup-y sorts of tasks.
     *
     * It's worth noting that this transformation is pretty much one-way, so if an entity needs to be
     * denormalized and shipped back to the server, it might be better to avoid this.
     *
     * Again, recs are a combination of regular profile user data and contextual data between the
     * profile user and the logged-in user. Since it's read-only and we don't care about the
     * distinction in the first place, we flatten it here and treat it as a single entity.
     */
    processStrategy(value: any) {
      const { user, ...rec } = value;

      const profile = {
        ...rec,
        ...user,
      };

      const {
        experiment_info: experimentInfo,
        spotify_theme_track: spotifyThemeTrack,
        spotify_top_artists: spotifyTopArtists,
      } = profile;

      // Other user endpoint returns different format
      if (spotifyThemeTrack) {
        delete profile.spotify_theme_track;
        set(profile, ['spotify', 'spotify_theme_track'], spotifyThemeTrack);
      }

      if (spotifyTopArtists) {
        delete profile.spotify_theme_track;
        set(profile, ['spotify', 'spotify_top_artists'], spotifyTopArtists);
      }

      if (experimentInfo?.user_interests) {
        delete profile.experiment_info;
        set(profile, ['user_interests'], experimentInfo.user_interests);
      }

      return profile;
    },
  }
);
export const profileArray = new ArraySchema(profileEntity);
